import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
    Users,
    Search,
    Plus,
    Pencil,
    Trash2,
    Mail,
    Copy,
    Check,
    X,
    Download,
    Upload,
    FileText,
    FileSpreadsheet,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ArrowLeft,
} from "lucide-react";
import Sidebar from "./company_Dashboard_sidebar";
import Starfield from "./Starfield";
import { GROWTHOS_BASE } from "../config/api";

/* ==========================================================
   TEAM PAGE — standalone page for the sidebar's "Team" nav
   item (/team). Same data + functionality (invite, edit,
   remove, resend invite, import, export) as the "Team & Users"
   tab inside Business Workspace — both read/write the same
   GET/POST/PATCH/DELETE /team-members endpoints, so changes
   made from either page show up in both.
========================================================== */

// import.meta.env is Vite-only syntax — this is a CRA app, so that check
// always failed silently and fell straight to localhost in production.
// Base URL now comes from the shared config (reads REACT_APP_API_BASE_URL).
const API_BASE = GROWTHOS_BASE;
const AUTH_TOKEN_KEY = "growthos_token";

const authHeaders = () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// main.py's global HTTPException handler normalizes every error response to
// {"success": false, "message": "..."} (see http_exception_handler in
// main.py) — so on a non-2xx we read that real message instead of throwing
// a generic "<Action> failed (<status>)" string. We also attach `.status`
// to the thrown Error so callers can special-case 403s (permission denied —
// e.g. a non-admin teammate trying to invite/edit/remove, blocked by
// require_team_permission("admin")/require_owner on the backend) with a
// friendlier banner instead of a raw error dump.
const apiError = async (verb, path, res) => {
    let message = `${verb} ${path} failed (${res.status})`;
    try {
        const body = await res.json();
        if (body?.message) message = body.message;
    } catch {
        // body wasn't JSON (e.g. a proxy/502 HTML page) — keep the fallback
    }
    const err = new Error(message);
    err.status = res.status;
    return err;
};
const apiGet = async (path) => {
    const res = await fetch(`${API_BASE}${path}`, { headers: { ...authHeaders() } });
    if (!res.ok) throw await apiError("GET", path, res);
    return res.json();
};
const apiPost = async (path, body) => {
    const res = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await apiError("POST", path, res);
    return res.json();
};
const apiPatch = async (path, body) => {
    const res = await fetch(`${API_BASE}${path}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await apiError("PATCH", path, res);
    return res.json();
};
const apiDelete = async (path) => {
    const res = await fetch(`${API_BASE}${path}`, {
        method: "DELETE",
        headers: { ...authHeaders() },
    });
    if (!res.ok) throw await apiError("DELETE", path, res);
    return res.json();
};

// Friendly copy for a blocked action — require_owner blocks every teammate
// (only the owner may hit /account, /account/change-password, /company-profile,
// not relevant here) while require_team_permission("admin") blocks any
// non-admin teammate from inviting/editing/removing/resending on
// /team-members*. Either way it's a 403, so we show one consistent message
// unless the backend's own detail is more specific.
const friendlyErrorMessage = (err, fallback) => {
    if (err?.status === 403) {
        return err.message && !/^\w+ \/.*failed \(\d+\)$/.test(err.message)
            ? err.message
            : "You don't have permission to do this — ask a workspace admin or the owner.";
    }
    return err?.message || fallback;
};

const DEPARTMENT_OPTIONS = ["Sales", "Marketing", "Support", "Admin", "Operations", "Finance"];
const STATUS_TINTS = { Active: "#22c55e", Invited: "#f59e0b", Inactive: "#6b7280" };
const PRODUCT_TINTS = ["#3b82f6", "#a855f7", "#22c55e", "#f97316", "#ec4899"];
const tintForIndex = (i) => PRODUCT_TINTS[i % PRODUCT_TINTS.length];
const initialsFor = (name) =>
    (name || "").trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
const emptyMemberForm = { name: "", email: "", department: "", role: "", status: "Active", permission_level: "member" };

// permission_level (viewer/member/admin) is a DIFFERENT field from the
// free-text `role` above (e.g. "Sales Manager" — just a job-title label,
// never checked by the backend). permission_level is what
// require_team_permission("admin") on the backend actually enforces —
// only 'admin' teammates can invite/edit/remove/resend; 'viewer' and
// 'member' can still see the team list (GET /team-members is open to
// everyone) but any write attempt gets a 403.
const PERMISSION_OPTIONS = [
    { value: "viewer", label: "Viewer", hint: "Can view the team list only" },
    { value: "member", label: "Member", hint: "Can view the team list only" },
    { value: "admin", label: "Admin", hint: "Can invite, edit & remove teammates" },
];
const PERMISSION_TINTS = { admin: "#f97316", member: "#3b82f6", viewer: "#6b7280" };
const permissionLabel = (v) => PERMISSION_OPTIONS.find((p) => p.value === v)?.label || "Member";

const cellStr = (v) => String(v ?? "").trim();
const csvEscapeField = (val) => {
    const s = String(val ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const buildCsv = (headerRow, dataRows) =>
    [headerRow, ...dataRows].map((row) => row.map(csvEscapeField).join(",")).join("\r\n");
const downloadCsv = (filename, csvText) => {
    const blob = new Blob(["\uFEFF" + csvText], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
const parseCsvRows = (text) => {
    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;
    const pushField = () => {
        row.push(field);
        field = "";
    };
    const pushRow = () => {
        pushField();
        rows.push(row);
        row = [];
    };
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQuotes) {
            if (c === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                field += c;
            }
        } else if (c === '"') {
            inQuotes = true;
        } else if (c === ",") {
            pushField();
        } else if (c === "\n") {
            pushRow();
        } else if (c === "\r") {
            // skip
        } else {
            field += c;
        }
    }
    if (field.length > 0 || row.length > 0) pushRow();
    return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
};

const IconBadge = ({ icon: Icon, tint }) => (
    <div
        className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${tint}1f`, color: tint }}
    >
        <Icon size={16} />
    </div>
);

/* ---------- Invite / Edit modal ---------- */
const TeamMemberFormModal = ({ member, onClose, onSave, onRemove, onResendInvite, saving, removing, resending }) => {
    const [form, setForm] = useState(() =>
        member
            ? {
                  name: member.name || "",
                  email: member.email || "",
                  department: member.department || "",
                  role: member.role || "",
                  status: member.status || "Active",
                  permission_level: member.permission_level || "member",
              }
            : emptyMemberForm
    );
    const field = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
    const canSave = form.name.trim().length > 0 && /\S+@\S+\.\S+/.test(form.email.trim());

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <div className="relative z-10 w-full max-w-md bg-[#111111] border border-orange-600/20 rounded-xl p-5 shadow-2xl max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <p className="text-white font-semibold">{member ? "Edit Team Member" : "Invite Member"}</p>
                    <button onClick={onClose} className="text-gray-500 hover:text-white transition">
                        <X size={18} />
                    </button>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="text-xs text-gray-500 mb-1 block">Name</label>
                        <input
                            value={form.name}
                            onChange={field("name")}
                            placeholder="e.g. John Doe"
                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-200 outline-none focus:border-orange-500/40"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-gray-500 mb-1 block">Email</label>
                        <input
                            value={form.email}
                            onChange={field("email")}
                            type="email"
                            disabled={!!member}
                            placeholder="e.g. john@company.com"
                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-200 outline-none focus:border-orange-500/40 disabled:opacity-60"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Department</label>
                            <select
                                value={form.department}
                                onChange={field("department")}
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-200 outline-none focus:border-orange-500/40"
                            >
                                <option value="">Select…</option>
                                {DEPARTMENT_OPTIONS.map((d) => (
                                    <option key={d} value={d}>
                                        {d}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Role</label>
                            <input
                                value={form.role}
                                onChange={field("role")}
                                placeholder="e.g. Sales Manager"
                                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-200 outline-none focus:border-orange-500/40"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs text-gray-500 mb-1 block">Permission Level</label>
                        <select
                            value={form.permission_level}
                            onChange={field("permission_level")}
                            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-200 outline-none focus:border-orange-500/40"
                        >
                            {PERMISSION_OPTIONS.map((p) => (
                                <option key={p.value} value={p.value}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                        <p className="text-[11px] text-gray-500 mt-1">
                            {PERMISSION_OPTIONS.find((p) => p.value === form.permission_level)?.hint}
                            {" — this controls actual access, separate from the \"Role\" label above."}
                        </p>
                    </div>

                    {member && (
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Status</label>
                            {member.status === "Invited" ? (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg px-3 py-2.5">
                                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                                        <span className="text-sm text-gray-300">Invited — waiting for them to set a password</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={onResendInvite}
                                        disabled={resending}
                                        className="flex items-center gap-1.5 text-xs text-gray-300 border border-white/10 hover:border-orange-500/40 hover:text-orange-400 transition px-3 py-1.5 rounded-lg disabled:opacity-50"
                                    >
                                        <Mail size={13} />
                                        {resending ? "Resending…" : "Resend Invite"}
                                    </button>
                                </div>
                            ) : (
                                <select
                                    value={form.status}
                                    onChange={field("status")}
                                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-200 outline-none focus:border-orange-500/40"
                                >
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-2 mt-5">
                    {member ? (
                        <button
                            onClick={() => onRemove(member)}
                            disabled={removing || saving}
                            className="px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition disabled:opacity-50 flex items-center gap-1.5"
                        >
                            <Trash2 size={14} />
                            {removing ? "Removing…" : "Remove"}
                        </button>
                    ) : (
                        <span />
                    )}
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white transition">
                            Cancel
                        </button>
                        <button
                            onClick={() => canSave && onSave(form)}
                            disabled={!canSave || saving || removing}
                            className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:hover:bg-orange-500 text-white transition"
                        >
                            {saving ? "Saving…" : member ? "Save Changes" : "Send Invite"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ---------- Banners ---------- */
const InviteResultBanner = ({ result, onDismiss }) => {
    const [copied, setCopied] = useState(false);
    if (!result) return null;
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(result.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            // clipboard write can fail silently (permissions) — non-critical
        }
    };
    const failed = /fail/i.test(result.message || "");
    return (
        <div className={`rounded-xl px-4 py-3 flex items-start gap-3 border ${failed ? "bg-red-500/10 border-red-500/30" : "bg-orange-500/10 border-orange-600/20"}`}>
            <Mail size={16} className={`mt-0.5 shrink-0 ${failed ? "text-red-400" : "text-orange-500"}`} />
            <div className="min-w-0 flex-1">
                <p className={`text-xs font-medium ${failed ? "text-red-400" : "text-orange-400"}`}>{result.message}</p>
                <div className="flex items-center gap-2 mt-2">
                    <input
                        readOnly
                        value={result.link}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-gray-400 outline-none"
                    />
                    <button
                        type="button"
                        onClick={copyLink}
                        className="flex items-center gap-1.5 text-xs text-gray-300 border border-white/10 hover:border-orange-500/40 hover:text-orange-400 transition px-2.5 py-1.5 rounded-lg shrink-0"
                    >
                        {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                        {copied ? "Copied" : "Copy"}
                    </button>
                </div>
            </div>
            <button onClick={onDismiss} className="text-gray-500 hover:text-white transition shrink-0">
                <X size={14} />
            </button>
        </div>
    );
};

const ImportSummaryBanner = ({ summary, onDismiss }) => {
    if (!summary) return null;
    const hasFailures = summary.failed.length > 0 || summary.error;
    return (
        <div className={`rounded-xl px-4 py-3 flex items-start gap-3 border ${hasFailures ? "bg-red-500/10 border-red-500/30" : "bg-orange-500/10 border-orange-600/20"}`}>
            <Upload size={16} className={`mt-0.5 shrink-0 ${hasFailures ? "text-red-400" : "text-orange-500"}`} />
            <div className="min-w-0 flex-1">
                {summary.error ? (
                    <p className="text-xs font-medium text-red-400">{summary.error}</p>
                ) : (
                    <>
                        <p className={`text-xs font-medium ${hasFailures ? "text-red-400" : "text-orange-400"}`}>
                            {summary.invited} invited{summary.failed.length > 0 ? `, ${summary.failed.length} skipped` : ""} — invite emails sent for each successful row.
                        </p>
                        {summary.failed.length > 0 && (
                            <ul className="mt-1.5 space-y-0.5">
                                {summary.failed.map((f, i) => (
                                    <li key={i} className="text-[11px] text-gray-500">
                                        Row {f.line}: {f.reason}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                )}
            </div>
            <button onClick={onDismiss} className="text-gray-500 hover:text-white transition shrink-0">
                <X size={14} />
            </button>
        </div>
    );
};

const ActionErrorBanner = ({ message, onDismiss }) => {
    if (!message) return null;
    return (
        <div className="rounded-xl px-4 py-3 flex items-start gap-3 border bg-red-500/10 border-red-500/30">
            <X size={16} className="mt-0.5 shrink-0 text-red-400 rotate-45" />
            <p className="text-xs font-medium text-red-400 flex-1">{message}</p>
            <button onClick={onDismiss} className="text-gray-500 hover:text-white transition shrink-0">
                <X size={14} />
            </button>
        </div>
    );
};

/* ---------- Main list + toolbar (same functionality as the
   Business Workspace "Team & Users" tab) ---------- */
const TeamUsersPanel = ({ members, membersLoading, refetchMembers }) => {
    const [search, setSearch] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [activeMember, setActiveMember] = useState(null);
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [resending, setResending] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [inviteResult, setInviteResult] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importSummary, setImportSummary] = useState(null);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const fileInputRef = useRef(null);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const filtered = members.filter((m) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return `${m.name} ${m.email} ${m.department} ${m.role}`.toLowerCase().includes(q);
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [search, pageSize, members.length]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

    const closeModal = () => {
        setModalOpen(false);
        setActiveMember(null);
    };

    const handleSubmit = async (form) => {
        setSaving(true);
        setActionError(null);
        const payload = {
            name: form.name.trim(),
            email: form.email.trim(),
            department: form.department,
            role: form.role.trim(),
            status: form.status || "Active",
            permission_level: form.permission_level || "member",
        };
        try {
            if (activeMember) {
                await apiPatch(`/team-members/${activeMember.id}`, payload);
            } else {
                const res = await apiPost("/team-members", payload);
                const created = res?.data?.[0];
                if (created?.invite_link) {
                    setInviteResult({ link: created.invite_link, message: res.message });
                }
            }
            await refetchMembers();
            closeModal();
        } catch (err) {
            console.error("Failed to save team member:", err);
            setActionError(friendlyErrorMessage(err, "Couldn't save that team member — please try again."));
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (member) => {
        if (!window.confirm(`Remove ${member.name} from the team?`)) return;
        setRemoving(true);
        setActionError(null);
        try {
            await apiDelete(`/team-members/${member.id}`);
            await refetchMembers();
            closeModal();
        } catch (err) {
            console.error("Failed to remove team member:", err);
            setActionError(friendlyErrorMessage(err, "Couldn't remove that team member — please try again."));
        } finally {
            setRemoving(false);
        }
    };

    const handleResendInvite = async () => {
        if (!activeMember) return;
        setResending(true);
        setActionError(null);
        try {
            const res = await apiPost(`/team-members/${activeMember.id}/resend-invite`, {});
            const updated = res?.data?.[0];
            if (updated?.invite_link) {
                setInviteResult({ link: updated.invite_link, message: res.message });
            }
            await refetchMembers();
            closeModal();
        } catch (err) {
            console.error("Failed to resend invite:", err);
            setActionError(friendlyErrorMessage(err, "Couldn't resend that invite — please try again."));
        } finally {
            setResending(false);
        }
    };

    const handleExportCsv = () => {
        const header = ["Name", "Email", "Department", "Role", "Permission Level", "Status"];
        const rows = members.map((m) => [m.name, m.email, m.department, m.role, permissionLabel(m.permission_level), m.status]);
        const csv = buildCsv(header, rows);
        downloadCsv(`team-members-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    };

    const handleExportExcel = () => {
        const header = ["Name", "Email", "Department", "Role", "Permission Level", "Status"];
        const rows = members.map((m) => [m.name, m.email, m.department, m.role, permissionLabel(m.permission_level), m.status]);
        const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
        worksheet["!cols"] = [{ wch: 22 }, { wch: 30 }, { wch: 16 }, { wch: 20 }, { wch: 14 }, { wch: 12 }];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Team Members");
        XLSX.writeFile(workbook, `team-members-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const handleImportClick = () => fileInputRef.current?.click();

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;

        let rows;
        try {
            if (/\.(xlsx|xls)$/i.test(file.name)) {
                const buffer = await file.arrayBuffer();
                const workbook = XLSX.read(buffer, { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
            } else {
                const text = await file.text();
                rows = parseCsvRows(text);
            }
        } catch (err) {
            console.error("Failed to read import file:", err);
            setImportSummary({ invited: 0, failed: [], error: "Couldn't read that file — make sure it's a valid .csv or .xlsx file." });
            return;
        }

        rows = (rows || []).filter((r) => r.some((cell) => cellStr(cell) !== ""));
        if (rows.length === 0) {
            setImportSummary({ invited: 0, failed: [], error: "That file doesn't have any rows in it." });
            return;
        }

        const headerCells = rows[0].map((h) => cellStr(h).toLowerCase());
        const headerIdx = {
            name: headerCells.indexOf("name"),
            email: headerCells.indexOf("email"),
            department: headerCells.indexOf("department"),
            role: headerCells.indexOf("role"),
            // Accept either "permission" or "permission level" as the column
            // name — optional; rows without it default to 'member' below.
            permission: headerCells.findIndex((h) => h === "permission" || h === "permission level"),
        };
        const hasHeader = headerIdx.name !== -1 && headerIdx.email !== -1;
        const dataRows = hasHeader ? rows.slice(1) : rows;
        const col = hasHeader ? headerIdx : { name: 0, email: 1, department: 2, role: 3, permission: -1 };
        const normalizePermission = (raw) => {
            const v = cellStr(raw).toLowerCase();
            return ["viewer", "member", "admin"].includes(v) ? v : "member";
        };
        const parsedRows = dataRows.map((r, i) => ({
            line: i + (hasHeader ? 2 : 1), // 1-based, matching what someone would see in a spreadsheet
            name: cellStr(r[col.name]),
            email: cellStr(r[col.email]),
            department: col.department >= 0 ? cellStr(r[col.department]) : "",
            role: col.role >= 0 ? cellStr(r[col.role]) : "",
            permission_level: col.permission >= 0 ? normalizePermission(r[col.permission]) : "member",
        }));

        setImporting(true);
        setImportSummary(null);
        const existingEmails = new Set(members.map((m) => cellStr(m.email).toLowerCase()));
        const seenInThisFile = new Set();
        let invited = 0;
        const failed = [];

        for (const row of parsedRows) {
            if (!row.name || !row.email) {
                failed.push({ line: row.line, reason: "Missing name or email" });
                continue;
            }
            const emailKey = row.email.toLowerCase();
            if (existingEmails.has(emailKey)) {
                failed.push({ line: row.line, reason: "Already a team member — skipped" });
                continue;
            }
            if (seenInThisFile.has(emailKey)) {
                failed.push({ line: row.line, reason: "Duplicate email in this file — skipped" });
                continue;
            }
            seenInThisFile.add(emailKey);
            try {
                await apiPost("/team-members", {
                    name: row.name,
                    email: row.email,
                    department: row.department,
                    role: row.role,
                    status: "Invited",
                    permission_level: row.permission_level,
                });
                invited++;
            } catch (err) {
                // A 403 here (non-admin bulk-importing) will fail identically
                // for every remaining row — stop early instead of grinding
                // through the rest of the file with the same denial.
                if (err.status === 403) {
                    failed.push({ line: row.line, reason: friendlyErrorMessage(err, "Could not invite this row") });
                    break;
                }
                failed.push({ line: row.line, reason: err.message || "Could not invite this row" });
            }
        }

        await refetchMembers();
        setImporting(false);
        setImportSummary({ invited, failed });
    };

    return (
        <div className="h-full flex flex-col gap-5">
            {actionError && <ActionErrorBanner message={actionError} onDismiss={() => setActionError(null)} />}
            {inviteResult && <InviteResultBanner result={inviteResult} onDismiss={() => setInviteResult(null)} />}
            {importSummary && <ImportSummaryBanner summary={importSummary} onDismiss={() => setImportSummary(null)} />}

            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <IconBadge icon={Users} tint="#f97316" />
                    <div className="min-w-0">
                        <p className="text-white font-semibold text-base leading-none">Team & Users</p>
                        <p className="text-xs text-gray-500 mt-1.5">Invite members and manage roles & permissions.</p>
                    </div>
                </div>

                <div className="flex items-center gap-2.5 shrink-0">
                    <div className="relative">
                        <Search size={14} className="text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search members…"
                            className="bg-black/40 border border-white/10 focus:border-orange-500/40 rounded-lg pl-8 pr-3 py-2 text-sm text-gray-200 outline-none w-52 transition"
                        />
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        onChange={handleFileChange}
                        className="hidden"
                    />
                    <button
                        type="button"
                        onClick={handleImportClick}
                        disabled={importing}
                        title="Import members from a CSV or Excel file (columns: Name, Email, Department, Role, optional Permission Level — defaults to Member)"
                        className="flex items-center gap-1.5 text-sm text-gray-300 border border-white/10 hover:border-orange-500/40 hover:text-orange-400 transition px-3 py-2 rounded-lg disabled:opacity-50 shrink-0"
                    >
                        <Upload size={14} />
                        {importing ? "Importing…" : "Import"}
                    </button>

                    <div className="relative shrink-0">
                        <button
                            type="button"
                            onClick={() => setExportMenuOpen((v) => !v)}
                            disabled={members.length === 0}
                            title="Export the current list"
                            className="flex items-center gap-1.5 text-sm text-gray-300 border border-white/10 hover:border-orange-500/40 hover:text-orange-400 transition px-3 py-2 rounded-lg disabled:opacity-40"
                        >
                            <Download size={14} />
                            Export
                            <ChevronDown size={13} className={`transition ${exportMenuOpen ? "rotate-180" : ""}`} />
                        </button>
                        {exportMenuOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => setExportMenuOpen(false)} />
                                <div className="absolute right-0 top-full mt-1.5 z-20 w-44 bg-[#161616] border border-white/10 rounded-lg shadow-xl overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleExportCsv();
                                            setExportMenuOpen(false);
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-300 hover:bg-white/5 hover:text-orange-400 transition"
                                    >
                                        <FileText size={13} />
                                        Export as CSV
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleExportExcel();
                                            setExportMenuOpen(false);
                                        }}
                                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-300 hover:bg-white/5 hover:text-orange-400 transition"
                                    >
                                        <FileSpreadsheet size={13} />
                                        Export as Excel
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => setModalOpen(true)}
                        className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 transition text-white text-sm font-semibold px-3.5 py-2 rounded-lg shrink-0"
                    >
                        <Plus size={15} />
                        Invite Member
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-400">
                <span>Show</span>
                <div className="relative">
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="appearance-none bg-black/40 border border-white/10 hover:border-orange-500/30 focus:border-orange-500/50 focus:outline-none rounded-lg pl-3 pr-7 py-1.5 text-sm text-gray-200 transition cursor-pointer"
                    >
                        {[10, 25, 50, 100].map((n) => (
                            <option key={n} value={n} className="bg-[#111]">
                                {n}
                            </option>
                        ))}
                    </select>
                    <ChevronDown size={13} className="text-gray-500 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <span>entries</span>
            </div>

            <div className="flex-1 min-h-0 bg-[#0d0d0d] border border-white/[0.08] rounded-xl overflow-hidden flex flex-col">
                <div className="grid grid-cols-[1.4fr_1.6fr_0.9fr_1fr_0.8fr_0.8fr_auto] gap-3 px-5 py-3.5 text-[11px] font-bold uppercase tracking-wider text-gray-400 bg-white/[0.03] border-b border-orange-500/10">
                    <span>Name</span>
                    <span>Email</span>
                    <span>Department</span>
                    <span>Role</span>
                    <span>Permission</span>
                    <span>Status</span>
                    <span className="text-right pr-2">Actions</span>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto">
                    {membersLoading ? (
                        <p className="text-sm text-gray-500 text-center py-10">Loading team members…</p>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-14 px-5">
                            <p className="text-sm text-gray-300 font-medium">{members.length === 0 ? "No team members yet" : "No members match your search"}</p>
                            <p className="text-xs text-gray-500 mt-1">
                                {members.length === 0 ? "Invite your first teammate to give them access to this workspace." : "Try a different name, email, or department."}
                            </p>
                        </div>
                    ) : (
                        paginated.map((m, i) => {
                            const tint = tintForIndex(i);
                            const statusTint = STATUS_TINTS[m.status] || STATUS_TINTS.Active;
                            return (
                                <div key={m.id} className="grid grid-cols-[1.4fr_1.6fr_0.9fr_1fr_0.8fr_0.8fr_auto] gap-3 px-5 py-3.5 items-center border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02] transition">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div
                                            className="h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
                                            style={{ backgroundColor: `${tint}1f`, color: tint }}
                                        >
                                            {initialsFor(m.name)}
                                        </div>
                                        <span className="text-sm text-gray-200 truncate">{m.name}</span>
                                    </div>
                                    <span className="text-sm text-gray-400 truncate">{m.email}</span>
                                    <span className="text-sm text-gray-400 truncate">{m.department || "—"}</span>
                                    <span className="text-sm text-gray-300 truncate">{m.role || "—"}</span>
                                    <span
                                        className="inline-flex items-center gap-1.5 text-[11px] font-medium w-fit"
                                        style={{ color: PERMISSION_TINTS[m.permission_level] || PERMISSION_TINTS.member }}
                                    >
                                        <span
                                            className="h-1.5 w-1.5 rounded-full"
                                            style={{ backgroundColor: PERMISSION_TINTS[m.permission_level] || PERMISSION_TINTS.member }}
                                        />
                                        {permissionLabel(m.permission_level)}
                                    </span>
                                    <span>
                                        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: statusTint }}>
                                            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: statusTint }} />
                                            {m.status || "Active"}
                                        </span>
                                    </span>
                                    <div className="flex items-center justify-end gap-1">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setActiveMember(m);
                                                setModalOpen(true);
                                            }}
                                            className="p-1.5 text-gray-500 hover:text-orange-400 transition"
                                            aria-label={`Edit ${m.name}`}
                                        >
                                            <Pencil size={15} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {!membersLoading && filtered.length > 0 && (
                <div className="shrink-0 pt-4 flex items-center justify-between gap-3 flex-wrap text-sm">
                    <p className="text-gray-500">
                        Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length} member{filtered.length === 1 ? "" : "s"}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={safePage <= 1}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 hover:border-orange-500/40 hover:text-orange-400 transition disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-gray-300"
                        >
                            <ChevronLeft size={14} />
                            Previous
                        </button>
                        <span className="px-3 py-1.5 rounded-lg bg-orange-500 text-white text-xs font-semibold">{safePage}</span>
                        <span className="text-gray-500 text-xs">of {totalPages}</span>
                        <button
                            type="button"
                            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                            disabled={safePage >= totalPages}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-gray-300 hover:border-orange-500/40 hover:text-orange-400 transition disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:text-gray-300"
                        >
                            Next
                            <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            )}

            {modalOpen && (
                <TeamMemberFormModal
                    member={activeMember}
                    saving={saving}
                    removing={removing}
                    resending={resending}
                    onClose={closeModal}
                    onSave={handleSubmit}
                    onRemove={handleRemove}
                    onResendInvite={handleResendInvite}
                />
            )}
        </div>
    );
};

/* ---------- Page shell (Sidebar + Starfield, same layout
   pattern as company_dashboard.js) ---------- */
const TeamPage = () => {
    const navigate = useNavigate();
    const [teamMembers, setTeamMembers] = useState([]);
    const [teamMembersLoading, setTeamMembersLoading] = useState(true);

    const loadTeamMembers = async () => {
        setTeamMembersLoading(true);
        try {
            const res = await apiGet("/team-members");
            setTeamMembers(res?.data || []);
        } catch (err) {
            console.error("Failed to load team members:", err);
        } finally {
            setTeamMembersLoading(false);
        }
    };

    useEffect(() => {
        loadTeamMembers();
    }, []);

    return (
        <div className="relative min-h-screen w-full bg-[#050505] text-zinc-300">
            <Sidebar />
            <div className="relative min-h-screen w-full overflow-hidden lg:pl-64">
                <Starfield />
                <div className="relative z-10 mx-auto flex max-w-7xl flex-col px-4 py-10 sm:px-6 sm:py-14 lg:px-10" style={{ height: "calc(100vh - 5rem)" }}>
                    <div className="mb-4 shrink-0">
                        <button
                            type="button"
                            onClick={() => navigate("/company_home")}
                            className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3.5 py-2 text-sm font-medium text-gray-300 transition hover:border-orange-600/40 hover:bg-orange-600/10 hover:text-orange-400"
                        >
                            <ArrowLeft size={15} /> Back
                        </button>
                    </div>
                    <div className="min-h-0 flex-1">
                        <TeamUsersPanel members={teamMembers} membersLoading={teamMembersLoading} refetchMembers={loadTeamMembers} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeamPage;